import { useEffect, useState } from 'react';
import {
  DropDown,
  FlexColumn,
  TextField,
  FormControls,
  InfoTooltip,
} from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { AddTestForm } from 'shared/types';
import SeveritySelector from '../SeveritySelector';
import WhereClauseControl from '../WhereClauseControl';
import TableTestDescriptionField from './shared/TableTestDescriptionField';
import TestMetadataForm from './shared/TestMetadataForm';
import TimestampColumnFormField from './shared/TimestampColumnFormField';

const detectionUnitOptions = [
  { value: 'day', label: 'days' },
  { value: 'hour', label: 'hours' },
  { value: 'minute', label: 'minutes' },
  { value: 'week', label: 'weeks' },
];

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
  timestampColumns: { label: string; value: string }[];
};

const TablesFreshnessAnomalies = ({ methods, timestampColumns }: Props) => {
  const { register, control, watch, setValue } = methods;

  const [expanded, setExpanded] = useState(true);

  const detectionPeriod = watch('detectionPeriod');
  const detectionUnit = watch('detectionUnit');
  const detectionDaysBack = watch('detectionDaysBack');

  const detectionUnitLabel = detectionUnitOptions
    .find((o) => o.value === detectionUnit)
    ?.label.toLowerCase();

  useEffect(() => {
    if (detectionPeriod && detectionUnit && detectionDaysBack) {
      setValue(
        'description',
        `Checking for freshness anomalies by looking at a ${detectionPeriod} ${detectionUnitLabel} period and comparing it to last ${detectionDaysBack} days`,
      );
    } else {
      setValue('description', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectionPeriod, detectionUnit, detectionDaysBack]);

  return (
    <FormControls.TestAccordion
      summary="Event Freshness anomalies"
      expanded={expanded}
      onChange={(_, val) => setExpanded(val)}
    >
      <FlexColumn sx={{ gap: 3, p: 3 }}>
        <FormControls.FieldsWrapper>
          <Typography sx={{ fontWeight: 'bold' }}>
            Test configuration
          </Typography>

          <TimestampColumnFormField
            title="Event timestamp column"
            control={control}
            options={timestampColumns}
          />

          <TimestampColumnFormField
            title="Updated at timestamp column"
            control={control}
            options={timestampColumns}
            name="updatedAtTimestampColumn"
            placeholder="updated at timestamp column"
          />

          <FormControls.Row>
            <FormControls.FormText>
              Detect event freshness anomalies by looking at a
            </FormControls.FormText>
            <TextField
              sx={{ width: '50px' }}
              defaultValue={1}
              {...register('detectionPeriod')}
            />
            <Controller
              name="detectionUnit"
              control={control}
              defaultValue="day"
              render={({ field }) => (
                <DropDown
                  size="large"
                  options={detectionUnitOptions}
                  {...field}
                />
              )}
            />
            <FormControls.FormText>
              period and comparing it to the last
            </FormControls.FormText>
            <TextField
              sx={{ width: '50px' }}
              defaultValue={14}
              {...register('detectionDaysBack')}
            />
            <FormControls.FormText>days</FormControls.FormText>
          </FormControls.Row>
          <Controller
            name="severity"
            control={methods.control}
            render={({ field }) => <SeveritySelector {...field} />}
            defaultValue={{ type: 'error' }}
          />
        </FormControls.FieldsWrapper>

        <FormControls.Divider />

        <TableTestDescriptionField methods={methods} />

        <FormControls.FieldsWrapper>
          <FormControls.TestAccordion
            variant="secondary"
            summary={
              <Typography sx={{ fontWeight: 'bold' }}>
                Advanced configuration
              </Typography>
            }
          >
            <FlexColumn sx={{ pt: '18px', gap: '24px' }}>
              <FormControls.Row>
                <FormControls.FormText>Sensitivity:</FormControls.FormText>
                <TextField
                  sx={{ width: '50px' }}
                  defaultValue={3}
                  {...register('sensitivity')}
                />
                <InfoTooltip title="The number of standard deviations from the mean. A higher number increases the sensitivity, while a lower number decreases it. We recommend a range of 2 to 3.5 for optimal sensitivity" />
              </FormControls.Row>
              {watch('detectionUnit') === 'day' && (
                <FormControls.Row>
                  <FormControls.FormText>
                    Enable seasonality:
                  </FormControls.FormText>
                  <Controller
                    name="seasonality"
                    control={control}
                    defaultValue="no"
                    render={({ field }) => (
                      <DropDown
                        size="large"
                        options={[
                          { value: 'yes', label: 'Yes' },
                          { value: 'no', label: 'No' },
                        ]}
                        {...field}
                      />
                    )}
                  />
                  <InfoTooltip title="If enabled, daily values will be compared exclusively to the corresponding previous values of the same weekday" />
                </FormControls.Row>
              )}
              <FormControls.Row>
                <FormControls.FormText>Backfill days:</FormControls.FormText>
                <TextField
                  sx={{ width: '50px' }}
                  defaultValue={2}
                  {...register('backfillDays')}
                />
                <InfoTooltip title="To ensure accuracy, metrics are recalculated for the last 2 days upon every run and are cached to shorten test running time" />
              </FormControls.Row>
              <Controller
                name="whereClause"
                control={control}
                render={({ field }) => <WhereClauseControl {...field} />}
              />
            </FlexColumn>
          </FormControls.TestAccordion>
        </FormControls.FieldsWrapper>

        <TestMetadataForm methods={methods} />
      </FlexColumn>
    </FormControls.TestAccordion>
  );
};

export default TablesFreshnessAnomalies;
